// The client side of /api/runs (BUILD-BRIEF-R2 §4.5, BUILD-BRIEF-R3f §2, BUILD-BRIEF-R4 §3) —
// what the Agents page and the Run desk read.
//
// TWO THINGS THIS FILE IS CAREFUL ABOUT:
//   · `child_log` is answered by GET /api/runs/:id for a live run and is NOT part of the shared
//     `RunDetail` contract, so it is declared here as an additive view — never invented, never
//     required.
//   · `getRunSteps` is round 3f's route. A server that predates it answers 404 (the /api
//     catch-all's honest JSON), and the desk must read that as "none recorded yet", not as a
//     failure. `stepsUnavailable` is that test, in one place, so no page re-implements it.
import { qs, request, ApiError } from './client.js';
import type { ListResponse, Run, RunDetail, RunStep, RunStepsPage, SubjectKind } from '../../shared/types.js';

export interface ListRunsParams {
  skill?: string;
  status?: string;
  /** The two travel together or not at all — the route says so and answers 400 otherwise. */
  subject_kind?: SubjectKind;
  subject_id?: number;
  limit?: number;
}

export function listRuns(params: ListRunsParams = {}): Promise<ListResponse<Run>> {
  // Named one by one rather than spread: `qs` takes a value bag, and a typed interface has no
  // index signature. It also keeps this file the one place that decides what the list accepts.
  return request(`/runs${qs({
    skill: params.skill,
    status: params.status,
    subject_kind: params.subject_kind,
    subject_id: params.subject_id,
    limit: params.limit,
  })}`);
}

/**
 * GET /api/runs/:id. `child_log` is the tail of a live harness child's own stderr — present
 * only while the run is queued or running, and only when this process holds that child. The
 * desk shows it as exactly that: the child's output, not a report and not a claim.
 */
export interface RunDetailView extends RunDetail {
  child_log?: string[];
}

export function getRun(id: number): Promise<RunDetailView> {
  return request(`/runs/${id}`);
}

/**
 * GET /api/runs/:id/steps?after=<id> — the append-only account of a run, ascending, `after`
 * exclusive. The caller passes back the `last_id` it was given and appends what it receives;
 * nothing is ever re-read and nothing is de-duplicated client-side.
 */
export function getRunSteps(id: number, after = 0, limit = 500): Promise<RunStepsPage> {
  return request(`/runs/${id}/steps${qs({ after: after || undefined, limit })}`);
}

/** A server without the steps route (404) or with it stubbed (501) has recorded NO steps. That
 *  is a state of the record, not an error to shout about: the desk says so in words and keeps
 *  polling the run itself. Anything else is a real failure and is reported as one. */
export function stepsUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

/** THE ONE START VERB. `Research` on an organisation or a person (`skill: 'deep_research'`),
 *  `Draft this` on the Inbox. Answers as soon as the run is queued, not when it ends. */
export function startRun(input: { skill: string; subject_kind?: SubjectKind; subject_id?: number }): Promise<Run> {
  return request('/runs', { method: 'POST', body: JSON.stringify(input) });
}

export function abortRun(id: number): Promise<Run> {
  return request(`/runs/${id}/abort`, { method: 'POST' });
}

export type { RunStep };
